import { Eye, Loader2 } from "lucide-react";
import { useState } from "react";

import {
  type CarrierPickupBooking,
  type CarrierPickupBookingState,
  useCarrierPickupBookings,
} from "@/api/carrier";
import { TablePager, usePagedState } from "@/components/data-table";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PickupBookingDetailDialog } from "@/features/pickup-booking-detail-dialog";
import { hasHttpStatus } from "@/lib/api-error";
import { formatDateOnly } from "@/lib/format";
import {
  formatPickupBookingAddress,
  formatPickupBookingCustomerName,
  formatPickupBookingCustomerSummary,
} from "@/lib/pickup-booking-display";

const STATUS_FILTERS: Array<{
  value: CarrierPickupBookingState | "ALL";
  label: string;
}> = [
  { value: "ALL", label: "All states" },
  { value: "Scheduled", label: "Scheduled" },
  { value: "InProgress", label: "In progress" },
  { value: "Completed", label: "Completed" },
  { value: "Failed", label: "Failed" },
  { value: "Cancelled", label: "Cancelled" },
];

export default function PickupBookingsScreen() {
  const { page, pageSize, setPage, setPageSize, resetPage } = usePagedState();
  const [stateFilter, setStateFilter] = useState<
    CarrierPickupBookingState | "ALL"
  >("ALL");
  const bookings = useCarrierPickupBookings({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    state: stateFilter,
  });
  const [detail, setDetail] = useState<CarrierPickupBooking | null>(null);
  const rows = bookings.data?.data ?? [];
  const total = bookings.data?.pagination.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const forbidden = hasHttpStatus(bookings.error, 403);
  const hasVisibleRows = rows.length > 0 && !forbidden;
  const emptyMessage =
    stateFilter === "ALL"
      ? "No pickup bookings yet."
      : "No pickup bookings match the selected state.";

  return (
    <div className="carrier-page">
      <PageHeader title="Pickup bookings" />

      <Card className="overflow-hidden">
        <div className="flex items-center justify-end border-b p-3 sm:p-4">
          <Select
            value={stateFilter}
            onValueChange={(value) => {
              resetPage();
              setStateFilter(value as CarrierPickupBookingState | "ALL");
            }}
          >
            <SelectTrigger
              aria-label="Filter by pickup state"
              size="sm"
              className="w-full sm:w-44"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {bookings.isLoading && (
          <p
            role="status"
            className="flex items-center gap-2 p-4 text-sm text-muted-foreground"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading pickup bookings...
          </p>
        )}

        {bookings.isFetching && bookings.data && (
          <p
            role="status"
            className="border-b px-4 py-2 text-xs text-muted-foreground"
          >
            Refreshing pickup bookings...
          </p>
        )}

        {bookings.error && (
          <div
            role="alert"
            className="flex flex-wrap items-center gap-3 p-4 text-sm text-destructive"
          >
            <span>
              {forbidden
                ? "You do not have permission to view pickup bookings."
                : bookings.data
                  ? "Pickup bookings could not refresh. Showing the most recent results."
                  : "Pickup bookings are unavailable."}
            </span>
            {!forbidden && (
              <Button
                type="button"
                size="sm-touch"
                variant="outline"
                onClick={() => bookings.refetch()}
              >
                Retry
              </Button>
            )}
          </div>
        )}

        {!bookings.isLoading && !bookings.error && rows.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </p>
        )}

        {hasVisibleRows && (
          <>
            <ul
              aria-label="Pickup booking summaries"
              className="divide-y md:hidden"
            >
              {rows.map((booking) => (
                <li key={booking.id}>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto min-h-11 w-full items-start justify-start gap-3 rounded-none p-3 text-left whitespace-normal hover:bg-muted/50"
                    aria-label={`View pickup booking for ${formatDateOnly(
                      booking.scheduledDate
                    )}`}
                    onClick={() => setDetail(booking)}
                  >
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium">
                            {formatDateOnly(booking.scheduledDate)}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {formatSlot(booking)} ·{" "}
                            {booking.carrierAddOnService?.name ??
                              "Service unavailable"}
                          </p>
                        </div>
                        <StatusBadge status={booking.derivedState} />
                      </div>
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {formatPickupBookingAddress(booking)}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {formatPickupBookingCustomerSummary(booking)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {booking.attachedShipmentCount} active shipment
                        {booking.attachedShipmentCount === 1 ? "" : "s"}
                      </p>
                    </div>
                    <Eye className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                  </Button>
                </li>
              ))}
            </ul>

            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Time slot</TableHead>
                    <TableHead>Pickup service</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead className="text-right">Shipments</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-16" aria-label="Actions" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell className="whitespace-nowrap font-medium">
                        {formatDateOnly(booking.scheduledDate)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {formatSlot(booking)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {booking.carrierAddOnService?.name ?? "-"}
                      </TableCell>
                      <TableCell className="max-w-48 truncate text-xs">
                        {formatPickupBookingCustomerName(booking)}
                      </TableCell>
                      <TableCell className="max-w-64 truncate text-xs">
                        {formatPickupBookingAddress(booking)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {booking.attachedShipmentCount}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={booking.derivedState} />
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDetail(booking)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {bookings.data && !forbidden && (
          <div className="border-t p-3">
            <TablePager
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={bookings.isLoading || bookings.isFetching}
            />
          </div>
        )}
      </Card>

      {detail && (
        <PickupBookingDetailDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setDetail(null);
          }}
          booking={detail}
        />
      )}
    </div>
  );
}

function formatSlot(booking: CarrierPickupBooking): string {
  return booking.slot
    ? `${booking.slot.startTime}–${booking.slot.endTime}`
    : "Time unavailable";
}
